import { axiosPrivate } from '../api/axios';

/**
 * Log de conexión/desconexión de la clase en vivo.
 *
 * Los eventos se acumulan y se envían por lotes para no generar una petición
 * por evento; el panel de diagnóstico los lee desde el servidor.
 */
const FLUSH_DEBOUNCE_MS = 5000;
const MAX_BATCH = 50;

const isDev = () => process.env.NODE_ENV !== 'production';

let context = { sessionId: null, courseId: null };
let pending = [];
let flushTimer = null;
let flushing = false;

export const setLiveClassLogContext = ({ sessionId = null, courseId = null } = {}) => {
    context = { sessionId, courseId };
};

const sendBatch = async (events) => {
    const { sessionId } = context;
    if (!sessionId || !events.length) return;
    try {
        await axiosPrivate.post(`/api/zoom/sessions/${sessionId}/connection-logs`, {
            courseId: context.courseId || undefined,
            events,
        });
    } catch (err) {
        // El log no debe romper la clase: se descarta el lote si el server falla.
        if (isDev()) {
            console.warn('[LiveClass log] no se pudo enviar:', err?.message || err);
        }
    }
};

const flushLiveClassLogs = async () => {
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
    if (flushing || !pending.length) return;
    flushing = true;
    const batch = pending.slice(0, MAX_BATCH);
    pending = pending.slice(batch.length);
    try {
        await sendBatch(batch);
    } finally {
        flushing = false;
    }
    if (pending.length) {
        void flushLiveClassLogs();
    }
};

const scheduleFlush = () => {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        void flushLiveClassLogs();
    }, FLUSH_DEBOUNCE_MS);
};

/**
 * @param {string} event p. ej. 'join-ok', 'reconnecting', 'closed-unexpected'
 * @param {object} data detalles serializables (reason, attempt, state…)
 */
export const logLiveClassEvent = (event, data = {}) => {
    const entry = {
        event,
        at: new Date().toISOString(),
        reason: data.reason != null ? String(data.reason) : undefined,
        attempt: typeof data.attempt === 'number' ? data.attempt : undefined,
        networkLevel: typeof data.networkLevel === 'number' ? data.networkLevel : undefined,
        detail: data,
    };

    if (isDev()) {
        console.log(`[LiveClass] ${event}`, data);
    }

    pending.push(entry);
    if (pending.length >= MAX_BATCH) {
        void flushLiveClassLogs();
        return;
    }
    scheduleFlush();
};

if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
        void flushLiveClassLogs();
    });
}
